// Per-section search: the `in:<place>` scope the header magnifier / ⌘F opens the top search
// bar (MessageSearch) pre-filtered to. One shared controller so a header's Search button and
// the globally-mounted bar talk through a single entry point. See the PR for the Sprite contract.

export type SearchScopeKind = "channel" | "dm" | "agent";

export interface SearchScope {
  kind: SearchScopeKind;
  id: string;
  label: string; // resolved by MessageSearch, e.g. "#eng-relay" / "@Apex"
}

export interface ScopedSearchRequest {
  kind: SearchScopeKind;
  id: string;
  nonce: number; // bumped per open() so the same place re-triggers the bar's effect
}

// A whole result section (`in:Docs`), not a place: it has no id and its search runs only that leg.
export type SearchSectionFilter = "pages" | "tasks" | "skills";

export const SECTION_FILTER_LABEL: Record<SearchSectionFilter, string> = {
  pages: "Docs",
  tasks: "Tasks",
  skills: "Skills",
};

export interface SectionSearchRequest {
  section: SearchSectionFilter;
  nonce: number;
}

export interface RecentSearch {
  query: string;
  scope: SearchScope | null;
  section?: SearchSectionFilter;
}

const RECENTS_CAP = 6;
// How many recents the empty card shows (it keeps up to RECENTS_CAP in session memory).
export const RECENTS_SHOWN = 4;
const recentKey = (r: Omit<RecentSearch, "query">): string =>
  r.scope ? `${r.scope.kind}:${r.scope.id}` : (r.section ?? "");

class ScopedSearchController {
  request = $state<ScopedSearchRequest | null>(null);
  sectionRequest = $state<SectionSearchRequest | null>(null);
  // Session-only (not persisted): queries can be sensitive and recents are a convenience.
  recents = $state<RecentSearch[]>([]);
  private nonce = 0;

  open(scope: { kind: SearchScopeKind; id: string }): void {
    this.request = { kind: scope.kind, id: scope.id, nonce: ++this.nonce };
  }

  openSection(section: SearchSectionFilter): void {
    this.sectionRequest = { section, nonce: ++this.nonce };
  }

  // Newest first, deduped by query + scope/section, capped. Called when a result is opened.
  record(query: string, scope: SearchScope | null, section?: SearchSectionFilter): void {
    const q = query.trim();
    if (q.length < 2) return;
    const entry: RecentSearch =
      section && !scope ? { query: q, scope, section } : { query: q, scope };
    const rest = this.recents.filter((r) => !(r.query === q && recentKey(r) === recentKey(entry)));
    this.recents = [entry, ...rest].slice(0, RECENTS_CAP);
  }
}

export const scopedSearch = new ScopedSearchController();

// The single entry the standard header's Search button calls (contract with Sprite).
export function openScopedSearch(scope: { kind: SearchScopeKind; id: string }): void {
  scopedSearch.open(scope);
}

export function openSectionSearch(section: SearchSectionFilter): void {
  scopedSearch.openSection(section);
}
